import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram, Linkedin, Facebook } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import Logo from "@/components/Logo";

// Use the confirmed official profiles; allow build-time overrides and keep all three icons visible.
const socialProfiles = [
  { name: "Instagram", id: "instagram", Icon: Instagram, url: process.env.REACT_APP_INSTAGRAM_URL || "https://www.instagram.com/lenisnutrition?utm_source=qr" },
  { name: "LinkedIn", id: "linkedin", Icon: Linkedin, url: process.env.REACT_APP_LINKEDIN_URL || "https://www.linkedin.com/company/lenisnutrition" },
  { name: "Facebook", id: "facebook", Icon: Facebook, url: process.env.REACT_APP_FACEBOOK_URL },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message || "Subscribed!");
      setEmail("");
    } catch {
      toast.error(process.env.REACT_APP_DESIGN_PREVIEW === "true" ? "Contact help@lenisnutrition.co.uk to join LENIS." : "We couldn’t complete your sign-up. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-white/10 bg-[#0A0A0A]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Logo testId="footer-brand-logo" variant="full" />
            <p className="mt-4 text-sm text-[#A1A1A6] leading-relaxed">
              A gut-focused plant and egg white protein range in development. Final formulation,
              nutrition and label details are provided on the product label.
            </p>
            <nav aria-label="LENIS social media" className="mt-6 flex gap-3">
              {socialProfiles.map(({ name, id, Icon, url }) => {
                const profileUrl = /^https:\/\//i.test(url || "") ? url : undefined;
                const iconClass = "inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#F7F6F0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#D6A921]";
                return profileUrl ? (
                  <a key={id} href={profileUrl} target="_blank" rel="noopener noreferrer"
                    aria-label={`LENIS on ${name} (opens in a new tab)`}
                    data-testid={`footer-social-${id}`}
                    className={`${iconClass} hover:border-[#D6A921] hover:text-[#D6A921]`}>
                    <Icon size={18} aria-hidden="true" />
                  </a>
                ) : (
                  <span key={id} role="img" aria-label={`${name} — profile link pending`}
                    title={`${name} — profile link pending`}
                    data-testid={`footer-social-${id}`} className={iconClass}>
                    <Icon size={18} aria-hidden="true" />
                  </span>
                );
              })}
            </nav>
            <div className="mt-6 space-y-4 text-xs text-[#A1A1A6]" data-testid="footer-contact">
              <div data-testid="footer-address">
                <h4 className="font-mono-clinical text-xs uppercase tracking-[0.2em] text-[#D6A921]">Address</h4>
                <address className="mt-3 not-italic leading-relaxed text-[#A1A1A6]">
                  LENIS Nutrition<br />
                  Flat 79 Hatton Place<br />
                  Luton, LU2 0FD<br />
                  United Kingdom
                </address>
                <p className="mt-2 font-mono-clinical text-xs uppercase tracking-widest text-[#D6A921]">Made in the UK</p>
              </div>
              <div className="space-y-1">
                <a href="mailto:help@lenisnutrition.co.uk" className="block hover:text-white break-all" data-testid="footer-care-email">help@lenisnutrition.co.uk</a>
                <p>Partnerships: <a href="mailto:partner@lenisnutrition.co.uk" className="hover:text-white break-all" data-testid="footer-partner-email">partner@lenisnutrition.co.uk</a></p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-mono-clinical text-xs uppercase tracking-[0.2em] text-[#D6A921]">
              Shop
            </h4>
            <ul className="mt-4 space-y-3 text-sm text-[#F7F6F0]">
              <li><Link to="/product/lenis-pea-brown-rice" className="hover:text-white">Pure Brown Rice & Pea Protein Isolate</Link></li>
              <li><Link to="/product/lenis-egg-white" className="hover:text-white">Pure Egg White Protein Isolate</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono-clinical text-xs uppercase tracking-[0.2em] text-[#D6A921]">
              Company
            </h4>
            <ul className="mt-4 space-y-3 text-sm text-[#F7F6F0]">
              <li><Link to="/?section=compare" className="hover:text-white">Compare protein</Link></li>
              <li><Link to="/?section=standard" className="hover:text-white">Why LENIS Nutrition</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact & Support</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono-clinical text-xs uppercase tracking-[0.2em] text-[#D6A921]">
              Get 15% Off
            </h4>
            <p className="mt-4 text-sm text-[#A1A1A6]">
              Join the inner circle for gut-health tips & exclusive drops.
            </p>
            <form onSubmit={submit} className="mt-4 flex overflow-hidden rounded-full border border-white/15 focus-within:border-[#D6A921] transition-colors">
              <input
                aria-label="Email address for newsletter"
                autoComplete="email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="footer-newsletter-input"
                className="min-w-0 flex-1 bg-transparent px-4 py-3 text-sm text-white placeholder:text-[#A1A1A6] outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                aria-label="Sign up for newsletter" data-testid="footer-newsletter-submit"
                className="flex items-center justify-center bg-[#D6A921] px-4 text-[#080806] hover:bg-white transition-colors disabled:opacity-60"
              >
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-[#A1A1A6]">
          <p>© {new Date().getFullYear()} LENIS Nutrition · Nutrition. Performance. Everyday life.</p>
          <p className="font-mono-clinical">
            LENIS Nutrition
          </p>
        </div>
      </div>
    </footer>
  );
}


